import React, { FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CheckCircleOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import PublicActionPage from '../components/PublicActionPage';
import PublicFormField from '../components/PublicFormField';
import {
  RecommendationInvitationAccess,
  SiteRecommendationService,
  SubmitRecommendationRequest,
} from './site-recommendation.service';
import './recommendations.css';

const initialForm: SubmitRecommendationRequest = {
  fullName: '',
  currentPosition: '',
  companyName: '',
  testimonial: '',
  publicationConsent: false,
};

const RecommendationRequestPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [access, setAccess] = useState<RecommendationInvitationAccess | null>(
    null
  );
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validation, setValidation] = useState<
    Partial<Record<keyof SubmitRecommendationRequest, string>>
  >({});

  useEffect(() => {
    let active = true;
    document.title = 'Private Recommendation Request | ITJobOpportunities';
    const existingRobots = document.querySelector<HTMLMetaElement>(
      'meta[name="robots"]'
    );
    const robots = existingRobots ?? document.createElement('meta');
    const previousContent = existingRobots?.content;
    robots.name = 'robots';
    robots.content = 'noindex,nofollow,noarchive,nosnippet';
    if (!existingRobots) document.head.appendChild(robots);

    const verify = async () => {
      try {
        const invitation = await SiteRecommendationService.invitation(token);
        if (!active) return;
        setAccess(invitation);
        setForm((current) => ({ ...current, fullName: invitation.fullName }));
      } catch (requestError) {
        if (!active) return;
        setError(
          requestError instanceof Error
            ? requestError.message
            : 'This recommendation request is no longer available.'
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    void verify();
    return () => {
      active = false;
      if (existingRobots) {
        robots.content = previousContent ?? '';
      } else {
        robots.remove();
      }
    };
  }, [token]);

  const validate = (): boolean => {
    const next: typeof validation = {};
    if (!form.fullName.trim()) next.fullName = 'Enter your name.';
    if (!form.currentPosition.trim()) {
      next.currentPosition =
        access?.audience === 'JOB_SEEKER'
          ? 'Enter your current or target role.'
          : 'Enter your current position.';
    }
    if (form.testimonial.trim().length < 40) {
      next.testimonial = 'Please write at least 40 characters.';
    }
    if (!form.publicationConsent) {
      next.publicationConsent = 'Publication consent is required.';
    }
    setValidation(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setError(null);
    try {
      await SiteRecommendationService.submit(token, {
        ...form,
        fullName: form.fullName.trim(),
        currentPosition: form.currentPosition.trim(),
        companyName: form.companyName?.trim(),
        testimonial: form.testimonial.trim(),
      });
      setSubmitted(true);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Your recommendation could not be submitted.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="recommendation-request-state" role="status">
        <span className="home-loading-dot" aria-hidden="true" />
        <strong>Verifying Your Private Invitation…</strong>
      </main>
    );
  }

  if (!access) {
    return (
      <main className="recommendation-request-state">
        <LockOutlined />
        <h1>This Private Link Is Unavailable</h1>
        <p>{error}</p>
        <Link to="/">Return to ITJobOpportunities</Link>
      </main>
    );
  }

  if (submitted) {
    return (
      <main className="recommendation-request-state recommendation-request-state--success">
        <CheckCircleOutlined />
        <h1>Thank You for Your Recommendation</h1>
        <p>
          It has been sent for review. Nothing is published until an authorized
          administrator approves it.
        </p>
        <Link to="/">Visit ITJobOpportunities</Link>
      </main>
    );
  }

  const isJobSeeker = access.audience === 'JOB_SEEKER';

  return (
    <PublicActionPage
      eyebrow="Private Invitation"
      title="Share Your Experience with ITJobOpportunities"
      description={
        isJobSeeker
          ? 'Your perspective can help other technology professionals understand what to expect while exploring and applying for jobs.'
          : 'Your perspective can help recruiting professionals and technology candidates understand what to expect from the platform.'
      }
      icon={<SafetyCertificateOutlined />}
      steps={[
        {
          title: 'Access Verified',
          detail: `This request was sent privately to ${access.maskedEmail}.`,
        },
        {
          title: 'Write in Your Own Words',
          detail: isJobSeeker
            ? 'Describe your experience finding and applying for opportunities.'
            : 'Describe your experience with the site and its community.',
        },
        {
          title: 'Reviewed Before Publication',
          detail: 'An administrator decides whether it appears publicly.',
        },
      ]}
      asideNote="This one-use link expires automatically and is not listed in site navigation or search indexing."
      formEyebrow="Your Recommendation"
      formTitle="Tell Us About Your Experience"
      formDescription="All fields marked required must be completed before submission."
    >
      <form
        className="recommendation-request-form"
        onSubmit={submit}
        noValidate
      >
        {error ? (
          <p className="recommendation-request-error" role="alert">
            {error}
          </p>
        ) : null}
        <PublicFormField
          id="recommendation-full-name"
          label="Full Name *"
          error={validation.fullName}
        >
          <input
            className="recommendation-input"
            type="text"
            autoComplete="name"
            maxLength={120}
            value={form.fullName}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                fullName: event.target.value,
              }))
            }
          />
        </PublicFormField>
        <PublicFormField
          id="recommendation-current-position"
          label={
            isJobSeeker ? 'Current or Target Role *' : 'Current Position *'
          }
          hint={
            isJobSeeker
              ? 'For example: Backend Developer, QA Engineer, or Data Analyst.'
              : 'For example: Independent Tech Recruiter or Talent Acquisition Lead.'
          }
          error={validation.currentPosition}
        >
          <input
            className="recommendation-input"
            type="text"
            autoComplete="organization-title"
            maxLength={160}
            value={form.currentPosition}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                currentPosition: event.target.value,
              }))
            }
          />
        </PublicFormField>
        <PublicFormField
          id="recommendation-company"
          label={
            isJobSeeker
              ? 'Current or Most Recent Company'
              : 'Company or Professional Brand'
          }
        >
          <input
            className="recommendation-input"
            type="text"
            autoComplete="organization"
            maxLength={160}
            value={form.companyName}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                companyName: event.target.value,
              }))
            }
          />
        </PublicFormField>
        <PublicFormField
          id="recommendation-testimonial"
          label="Recommendation *"
          hint={`${form.testimonial.length}/2000 characters`}
          error={validation.testimonial}
        >
          <textarea
            className="recommendation-input"
            rows={7}
            minLength={40}
            maxLength={2000}
            value={form.testimonial}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                testimonial: event.target.value,
              }))
            }
          />
        </PublicFormField>
        <div className="recommendation-consent">
          <label htmlFor="recommendation-consent">
            <input
              id="recommendation-consent"
              type="checkbox"
              checked={form.publicationConsent}
              aria-invalid={Boolean(validation.publicationConsent) || undefined}
              aria-describedby={
                validation.publicationConsent
                  ? 'recommendation-consent-error'
                  : undefined
              }
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  publicationConsent: event.target.checked,
                }))
              }
            />
            <span>
              I authorize ITJobOpportunities to publish my name, role,
              {isJobSeeker
                ? ' current or most recent company'
                : ' company or professional brand'}
              , and recommendation.
            </span>
          </label>
          {validation.publicationConsent ? (
            <p id="recommendation-consent-error" role="alert">
              {validation.publicationConsent}
            </p>
          ) : null}
        </div>
        <button
          type="submit"
          className="public-button-primary recommendation-submit"
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : 'Submit for Review'}
        </button>
      </form>
    </PublicActionPage>
  );
};

export default RecommendationRequestPage;
